import React, { useRef, useState } from 'react'
import { Collapse, Typography } from '@mui/material'
import { AlertCircle, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { refinements } from '@helpers/refinements';
import BasePopper from '@reusables/BasePopper';

export default function BasePasswordRulesWrapper({children, value, collapse = false}: {children: React.ReactNode, value: string, collapse?: boolean}) {
    const {t} = useTranslation("", {keyPrefix: "general.validation.global.passwordRules"});
    // Define a state to store the anchor element for the Popper.
    const [popperAnchorEl, setPopperAnchorEl] = useState<HTMLDivElement | null>(null);

    // Define a ref for the div wrapping the input to use as an anchor for the Popper.
    const inputWrapperRef = useRef(null);

    const handleFocus = () => {
        // Use the current element as the anchor for the Popper.
        setPopperAnchorEl(inputWrapperRef.current);
    };

    const handleBlur = () => {
        setPopperAnchorEl(null);
    };

    // Define a function to evaluate the password rules.
    const evaluatePasswordRules = (password: string) => {
        const rules = [
            { id: 'length', text: t("0"), isValid: password && refinements.passwordLength.refine(password) },
            { id: 'letter', text: t("1"), isValid: password && refinements.passwordContainsCharacters.refine(password) },
            { id: 'number', text: t("2"), isValid: password && refinements.passwordContainsNumbers.refine(password) },
        ];

        return rules;
    };

    return (
        <div ref={inputWrapperRef} className="relative" onFocus={handleFocus} onBlur={handleBlur}>
            {children}
            {collapse ? (
                <Collapse in={Boolean(popperAnchorEl)}>
                    <div className="bg-[#fff] p-[14px] rounded-[8px] levitation font-thin text-sm space-y-[4px]">
                        {evaluatePasswordRules(value).map(rule => (
                            <div key={rule.id} className="flex items-center space-x-2">
                                {rule.isValid ? <Check size={16} className="text-accent" /> : <AlertCircle size={16} className="text-error" />}
                                <Typography variant="body2" className={rule.isValid ? "text-accent" : "text-error"}>{rule.text}</Typography>
                            </div>
                        ))}
                    </div>
                </Collapse>
            ) : (
                <BasePopper open={!!popperAnchorEl} anchorEl={popperAnchorEl} placement='bottom-start'>
                    <div className="bg-[#fff] p-[14px] rounded-[8px] levitation font-thin text-sm space-y-[4px]">
                        {evaluatePasswordRules(value).map(rule => (
                            <div key={rule.id} className="flex items-center space-x-2">
                                {rule.isValid ? <Check size={16} className="text-accent" /> : <AlertCircle size={16} className="text-error" />}
                                <Typography variant="body2" className={rule.isValid ? "text-accent" : "text-error"}>{rule.text}</Typography>
                            </div>
                        ))}
                    </div>
                </BasePopper>
            )}
        </div>
    )
}
